import { HttpClient } from '@angular/common/http';
import { Component, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { VERSION_INFO } from 'src/environments/version';
import { VideoUploadService } from 'src/app/shared/service/VideoUploadService.service';
import {
  LoggedInUserKeys,
  LoggedInUserService,
} from 'src/app/shared/service/logged-in-user.service';
import { AuthSessionService } from 'src/app/shared/service/auth-session.service';
import { Navigations } from 'src/app/shared/utils/navigations-enum';
import * as Sentry from '@sentry/angular';


interface VersionInfo {
  version: string;
  commit?: string;
  buildTime?: string;
}
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly _http: HttpClient,
    private readonly zone: NgZone,
    private readonly videoUploaderService: VideoUploadService,
    private readonly loggedInUserService: LoggedInUserService,
    private readonly authSessionService: AuthSessionService,
  ) {}

  // can add more routes to skip version check if needed
  private SKIP_VERSION_CHECK_ROUTES: RegExp[] = [
    /^\/examination\/test/,
    /^\/main\/interview-agent/,
  ];

  // Get deployed client from environment
  deployedClient = environment.deployedClient;

  macUpdateBanner: { version: string; downloadUrl: string } | null = null;
  winUpdateAvailable: { version: string } | null = null;
  winUpdateReady = false;
  winDownloadProgress: number | null = null;
  winDownloadSpeed: string | null = null;
  winDownloadEta: number | null = null;
  updateError: string | null = null;
   updateErrorTimer: any = null;
   remindLaterTimer: any = null;

  dismissMacUpdateBanner(): void {
    this.macUpdateBanner = null;
  }

  restartAndInstall(): void {
    (window as any).electron?.ipcRenderer?.send('install-update');
  }

  openDownloadLink(): void {
    if (this.macUpdateBanner?.downloadUrl) {
      window.open(this.macUpdateBanner.downloadUrl, '_blank');
    }
  }

  dismissDownloading(): void {
    this.winUpdateAvailable = null;
    this.winDownloadProgress = null;
    this.winDownloadSpeed = null;
    this.winDownloadEta = null;
  }

  formatEta(sec: number | null): string {
    if (sec === null || sec <= 0) return '';
    if (sec < 60) return `${sec}s left`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s left`;
  }

  remindLater(): void {
    this.winUpdateReady = false;
    clearTimeout(this.remindLaterTimer);
    this.remindLaterTimer = setTimeout(() => {
      this.zone.run(() => { this.winUpdateReady = true; });
    }, 5 * 60 * 1000);
  }

  showUpdateNow(): void {
    clearTimeout(this.remindLaterTimer);
    this.remindLaterTimer = null;
    this.winUpdateReady = true;
  }

  async ngOnInit(): Promise<void> {
    const currentUrl = window.location.pathname;

    // Clear any stale exam-in-progress marker from a prior crash; the attempt
    // screen re-sets it, and a leftover 'true' would stall the close handler.
    try {
      localStorage.removeItem('isExamInProgress');
    } catch (e) {}

    // Sessions from before the cookie switch still keep their token in storage: move them
    // onto the HttpOnly cookie. Not awaited; until it lands, requests keep using Bearer.
    void this.authSessionService.upgradeLegacySession();

    if (currentUrl.includes('/assessment/public')) {
      return;
    }

    const electron = (window as any).electron;
    if (electron?.ipcRenderer) {
      electron.ipcRenderer.receive('update-available-mac', (data: { version: string; downloadUrl: string }) => {
        this.zone.run(() => { this.macUpdateBanner = data; });
      });

      electron.ipcRenderer.receive('update-available', (info: { version: string }) => {
        this.zone.run(() => { this.winUpdateAvailable = { version: info.version }; });
      });

      electron.ipcRenderer.receive('update-progress', (data: { percent: number; speedMBps: string; etaSec: number | null }) => {
        this.zone.run(() => {
          this.winDownloadProgress = data.percent;
          this.winDownloadSpeed = data.speedMBps;
          this.winDownloadEta = data.etaSec;
        });
      });

      electron.ipcRenderer.receive('update-downloaded', () => {
        this.zone.run(() => {
          this.winUpdateAvailable = null;
          this.winDownloadProgress = null;
          this.winDownloadSpeed = null;
          this.winDownloadEta = null;
          this.winUpdateReady = true;
        });
      });

      electron.ipcRenderer.receive('update-error', (message: string) => {
        this.zone.run(() => {
          this.updateError = message;
          clearTimeout(this.updateErrorTimer);
          this.updateErrorTimer = setTimeout(() => {
            this.zone.run(() => { this.updateError = null; });
          }, 6000);
        });
      });
    }

    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get('token')?.replace(/ /g, '+');

    if (token) {
      console.log('Token found in app.component.ts:', token);
      sessionStorage.setItem('token', token);
    }

    // Crash/close recovery: re-drive a lite submission whose payload is still in
    // IndexedDB by routing to the success page (its sync flow finishes it).
    await this.recoverPendingLiteSubmission();

    await this.checkVersionMismatch();
    setInterval(async () => {
      if (!this.shouldSkipVersionCheck()) {
        await this.checkVersionMismatch();
      }
    }, 10 *  30 * 1000); // every 5 minutes
  }

  /**
   * Re-drives a lite submission captured to IndexedDB whose submit never fired
   * (crash/close after exam, or closed offline). Only routes to the success page
   * — never submits directly — so its sync pipeline stays the single submitter
   * and there's no double-submission risk.
   */
  private async recoverPendingLiteSubmission(): Promise<void> {
    // Only the lite desktop build persists a pending submission locally.
    if (!environment.DESKTOP_IQIGAI_LITE) return;

    try {
      // sessionStorage is wiped on reopen; fall back to the localStorage mirror.
      const isLiteDrive =
        (sessionStorage.getItem('isLiteAppDrive') ??
          localStorage.getItem('isLiteAppDrive')) === 'true';
      if (!isLiteDrive) {
        Sentry.logger.info('Lite submission recovery: skipped, isLiteAppDrive not true', {
          sessionIsLiteAppDrive: sessionStorage.getItem('isLiteAppDrive'),
          localIsLiteAppDrive: localStorage.getItem('isLiteAppDrive'),
        });
        return;
      }

      // Without auth the authGuard bounces us to login and recovery can't run.
      const user = this.loggedInUserService.getLoggedInUser(
        LoggedInUserKeys.LOGGED_IN_USER,
      );
      if (!user || !this.loggedInUserService.isLoggedIn()) {
        Sentry.logger.warn('Lite submission recovery: skipped, no auth session found');
        return;
      }

      // dbPromise is set on a 100ms timer in the service ctor; wait so an early
      // read doesn't falsely conclude "nothing pending".
      await this.waitForIndexedDbReady();

      const pendingPayload = await this.videoUploaderService.getItem<any>(
        'pendingSubmitPayload',
      );
      // A crash after submit but during the chunk drain leaves only this marker
      // (no payload) — still route so the success page can finish the chunks.
      const chunksPendingDrain =
        localStorage.getItem('liteChunksPendingDrain') === 'true';
      if (!pendingPayload && !chunksPendingDrain) {
        Sentry.logger.info('Lite submission recovery: skipped, nothing pending in IndexedDB/disk', {
          assessmentDriveSetId: String(localStorage.getItem('assessmentDriveSetId')),
        });
        return;
      }

      if (pendingPayload) {
        // Getting here means the payload never reached the server on its own.
        // Tag it so the backend can tell recovery apart from a normal submit.
        pendingPayload.wasRecoveredAfterClose = true;
        await this.videoUploaderService.setItem('pendingSubmitPayload', pendingPayload);
      }

      Sentry.logger.info(
        'Lite submission recovery on app open: routing to success page',
        {
          reason: pendingPayload ? 'pendingSubmitPayload' : 'chunksPendingDrain',
          navigatorOnLine: navigator.onLine,
          assessmentDriveSetId: String(
            localStorage.getItem('assessmentDriveSetId'),
          ),
        },
      );

      this.zone.run(() => {
        this.router.navigate([Navigations.SUBMIT_SUCCESS_PAGE]);
      });
    } catch (error) {
      // Recovery is best-effort — never let it block app startup.
      Sentry.captureException(error, {
        tags: { stage: 'lite-submission-recovery' },
      });
    }
  }

  /** Waits up to ~1s for VideoUploadService to open IndexedDB (dbPromise is set on a 100ms timer). */
  private async waitForIndexedDbReady(): Promise<void> {
    for (let i = 0; i < 20; i++) {
      if ((this.videoUploaderService as any).dbPromise) return;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  private shouldSkipVersionCheck(): boolean {
    const currentUrl = this.router.url.split('?')[0];

    return this.SKIP_VERSION_CHECK_ROUTES.some(pattern =>
      pattern.test(currentUrl)
    );
  }

  private async checkVersionMismatch(): Promise<void> {
    // 1️⃣ Skip version check for LOCAL clients
    if(this.deployedClient == 'LOCAL_MAIN' || this.deployedClient == 'LOCAL_LITE') {
        return;
      }
    try {
      // 2️⃣ Load BE version
      // _t makes every call a new URL so the browser can't answer from its cache
      // (nginx serves this as a static file with no Cache-Control)
      const beVersion = await firstValueFrom(
        this._http.get<VersionInfo>('/app/api/version', {
          params: { _t: Date.now().toString() },
        }),
      );

      // const beVersion: VersionInfo = {
      //   version: '1.0.8'
      // };

      if (!VERSION_INFO.version || !beVersion?.version) {
        return;
      }

      const lastSeenBeVersion = sessionStorage.getItem('app_version');

      // 3️⃣ Compare
      // both sides hold sha256("IQIGAI" + "x.y.z") from src/scripts/version_generator.js,
      // a one-way hash, so they are matched as-is
      if (VERSION_INFO.version !== beVersion.version) {
        if (lastSeenBeVersion !== beVersion.version) {
          const shouldReload = window.confirm(
            '⚠️ New version detected.\n\n' +
              'If you are in the middle of an assessment,please cancel as your progress may be lost.\n\n' +
              'Click OK to refresh now, or Cancel to continue.',
          );

          if (shouldReload) {
            sessionStorage.setItem('app_version', beVersion.version);
            this.hardReload();
          }
        }
      }
    } catch (err) {
      // Never block the app due to version check
      console.error('Version check failed', err);
    }
  }

  hardReload(): void {
    const url = new URL(window.location.href);
    // Add cache-busting parameter
    url.searchParams.set('_t', Date.now().toString());
    window.location.href = url.toString();
    // alert('The application has been updated. The page will now reload to get the latest version.');
  }

  // loading: boolean=true;
  // dynamicComponent: Type<AttemptTestComponent>;
  // constructor(router: Router) {
  //   // setTimeout(() => {

  //     this.loading = false;
  //     router.events.subscribe(
  //       (event: any): any => {
  //         if (event instanceof NavigationStart) {
  //             this.loading = true;
  //           } else if (event instanceof NavigationEnd) {
  //             this.loading = false;
  //           }
  //         }
  //       );
  //   // }, 10000);

  // }
  // control = new FormControl();
  // async loadComponent(): Promise<any> {
  //   const imported = await import('src/app/examination/attempt-test/attempt-test.component');
  //   this.dynamicComponent = imported.AttemptTestComponent;
  //   this.dynamicComponent;

  // }
}
